/**
 * ログイン前の画面の帯（docs/95 ★2）：ログイン・会員登録・パスワード再設定・会社の選択の枠の上に、運営の案内を1件1行で出す。
 *   ・中身は GET /api/v1/notices（ログインなし・API の控えから。管理アプリの案内の「ログイン画面」オンのもの）
 *   ・1行＝区分の印（文字。色だけで伝えない）＋メンテナンスなら停止期間「10月30日（金）18:00〜24:00 はご利用いただけません」、ほかは題。
 *     本文は「詳細」で開く（ログイン前の画面に長い文を並べない）。閉じるボタンは無い（ホームの帯と違い、閉じられない）
 *   ・60 秒ごとに読み直す（PASSIVE＝自動ログアウトの操作に数えない）。停止期間に入ると API が 503 MAINTENANCE を返し、
 *     api.ts の reportMaintenance が止めの画面 e を重ねる（ログイン画面を開いたままでも 18:00 に切り替わる）
 *   ・#179（あとから入る中身で押す所をずらさない）：帯は枠の上に入るので、届くまで（1.5 秒まで）枠を描かない＝useNotices の ready
 *   文は contract の NOTICE_MSG（notices.ts）
 */
import { NOTICE_MSG, noticeLoginDetail, noticeLoginLine, noticeTone, type NoticesResponse, type PublicNotice } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { api } from "../api.js";
import { PASSIVE } from "../lib/autoLogout.js";

/** 届くのを待つ上限（#179。止まっても入力の枠は出す） */
const WAIT_MS = 1500;

/** ログイン前の案内。ready＝届いたか、待つ上限を過ぎた（枠を描いてよい） */
export function useNotices(enabled = true): { items: PublicNotice[]; ready: boolean } {
  const q = useQuery({
    queryKey: ["notices"],
    queryFn: () => api<NoticesResponse>("GET", "/notices", undefined, PASSIVE),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
  });
  const [over, setOver] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setOver(true), WAIT_MS);
    return () => window.clearTimeout(t);
  }, []);
  const items = [...(q.data?.items ?? [])].sort((a, b) => NOTICE_MSG.loginOrder.indexOf(a.kind) - NOTICE_MSG.loginOrder.indexOf(b.kind));
  return { items, ready: !enabled || !q.isPending || over };
}

function NoticeLine({ n }: { n: PublicNotice }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const detail = noticeLoginDetail(n);
  return (
    <section className={`nb-item is-${noticeTone(n.kind)}`} aria-label={NOTICE_MSG.region(n.kind)} data-testid="notice-band-item" data-kind={n.kind}>
      <div className="nb-row">
        <span className="nb-mark">{NOTICE_MSG.kind[n.kind]}</span>
        <span className="nb-line" data-testid="notice-band-line">
          {noticeLoginLine(n)}
        </span>
        {detail ? (
          <button type="button" className="btn text nb-more" aria-expanded={open} aria-controls={id} onClick={() => setOpen((x) => !x)} data-testid="notice-band-detail">
            {NOTICE_MSG.detail}
          </button>
        ) : null}
      </div>
      {open ? (
        <p className="nb-detail" id={id} data-testid="notice-band-body">
          {detail}
        </p>
      ) : null}
    </section>
  );
}

/** 帯（案内が無ければ何も描かない） */
export function NoticeBand({ items }: { items: PublicNotice[] }) {
  if (!items.length) return null;
  return (
    <div className="noticeband" data-testid="notice-band">
      {items.map((n) => (
        <NoticeLine key={n.id} n={n} />
      ))}
    </div>
  );
}
